// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a publication figure of the view (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`,
 * §Next S4): the globe as rendered or the whole frame on the Equal Earth
 * projection, with a title, the colour bar, optional pins and the citation
 * as its caption, at 300 dpi, as PNG and SVG.
 *
 * This module is the pure half, testable without a browser:
 *
 * - **Sizes** in millimetres and points, turned into pixels at the figure's
 *   dpi: one column (85 mm) or two (180 mm), the widths most journals give.
 * - **Layout**: where every line of text, the map, the colour bar and its
 *   ticks go, measured with the caller's text metrics so both files wrap
 *   the same way.
 * - **SVG**: text, colour bar, coastlines and pins as vectors; the map is
 *   an embedded PNG and stays a raster (the data are a grid of cells).
 * - **PNG metadata**: a pHYs chunk so the file opens at its physical size
 *   (300 dpi), and iTXt chunks with the title, caption and how the map was
 *   made (`withPngMetadata`).
 * - **Equal Earth** (Šavrič, Patterson & Jenny 2019, `equalEarth.ts`): the
 *   raster resampled row by row from a lon/lat sampler, the world's
 *   outline, graticule and coastlines as paths. Never Mercator.
 */

import { EE_X_MAX, EE_Y_MAX, equalEarth, equalEarthRow } from '../equalEarth'

export const FIGURE_DPI = 300
export type FigureSize = 'single' | 'double'
/** Column widths: 85 mm and 180 mm, as most journals in our domains give them (AGU, AMS, Elsevier, Nature). */
export const FIGURE_WIDTH_MM: Readonly<Record<FigureSize, number>> = { single: 85, double: 180 }

/** Millimetres at a resolution, in whole pixels. */
export function mmToPx(mm: number, dpi = FIGURE_DPI): number {
  return Math.round((mm / 25.4) * dpi)
}

/** Points (1/72 inch) at a resolution, in pixels. */
export function ptToPx(pt: number, dpi = FIGURE_DPI): number {
  return (pt * dpi) / 72
}

// ------------------------------------------------------------------ PNG metadata

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10]

let crcTable: Uint32Array | null = null

/** CRC-32 (ISO 3309, as PNG uses it) over bytes [start, end). */
export function crc32(bytes: Uint8Array, start = 0, end = bytes.length): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
      let c = n
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
      crcTable[n] = c >>> 0
    }
  }
  let c = 0xffffffff
  for (let i = start; i < end; i++) c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

interface Chunk {
  readonly type: string
  /** Offset of the chunk's length field. */
  readonly at: number
  readonly length: number
}

function u32(bytes: Uint8Array, at: number): number {
  return ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0
}

/** The chunks of a PNG, in order; throws when it is not one. */
export function pngChunks(png: Uint8Array): Chunk[] {
  if (png.length < 8 || PNG_SIGNATURE.some((b, i) => png[i] !== b)) throw new Error('not a PNG')
  const out: Chunk[] = []
  let at = 8
  while (at + 12 <= png.length) {
    const length = u32(png, at)
    const type = String.fromCharCode(png[at + 4], png[at + 5], png[at + 6], png[at + 7])
    if (at + 12 + length > png.length) throw new Error('truncated PNG')
    out.push({ type, at, length })
    at += 12 + length
    if (type === 'IEND') break
  }
  if (!out.length || out[0].type !== 'IHDR') throw new Error('PNG without IHDR')
  return out
}

function chunk(type: string, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length)
  const view = new DataView(out.buffer)
  view.setUint32(0, data.length)
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i)
  out.set(data, 8)
  view.setUint32(8 + data.length, crc32(out, 4, 8 + data.length))
  return out
}

/** Pixels per metre for a dpi (300 dpi is 11,811 px/m, as PNG stores it). */
export function pixelsPerMetre(dpi: number): number {
  return Math.round(dpi / 0.0254)
}

function physChunk(dpi: number): Uint8Array {
  const data = new Uint8Array(9)
  const view = new DataView(data.buffer)
  const ppm = pixelsPerMetre(dpi)
  view.setUint32(0, ppm)
  view.setUint32(4, ppm)
  data[8] = 1 // the unit is the metre
  return chunk('pHYs', data)
}

/** An uncompressed iTXt chunk: a Latin-1 keyword, UTF-8 text. */
function itxtChunk(keyword: string, text: string): Uint8Array {
  const key = keyword.replace(/[^\x20-\x7e]/g, '').slice(0, 79) || 'Comment'
  const body = new TextEncoder().encode(text)
  const data = new Uint8Array(key.length + 5 + body.length)
  for (let i = 0; i < key.length; i++) data[i] = key.charCodeAt(i)
  // keyword NUL, compression flag 0, method 0, empty language tag NUL, empty translated keyword NUL
  data.set([0, 0, 0, 0, 0], key.length)
  data.set(body, key.length + 5)
  return chunk('iTXt', data)
}

/**
 * The PNG with its physical resolution (pHYs, replacing any) and text
 * chunks (`Title`, `Description`, …; replacing any with the same keyword)
 * right after IHDR, so a layout program places it at its true size.
 */
export function withPngMetadata(png: Uint8Array, dpi: number, text: Readonly<Record<string, string>> = {}): Uint8Array {
  const chunks = pngChunks(png)
  const keys = new Set(Object.keys(text))
  const keyOf = (c: Chunk): string => {
    let s = ''
    for (let i = c.at + 8; i < c.at + 8 + c.length && png[i] !== 0; i++) s += String.fromCharCode(png[i])
    return s
  }
  const added = [physChunk(dpi), ...Object.entries(text).map(([k, v]) => itxtChunk(k, v))]
  const kept = chunks.filter((c) => c.type !== 'pHYs' && !((c.type === 'iTXt' || c.type === 'tEXt') && keys.has(keyOf(c))))
  const size = 8 + kept.reduce((n, c) => n + 12 + c.length, 0) + added.reduce((n, a) => n + a.length, 0)
  const out = new Uint8Array(size)
  out.set(PNG_SIGNATURE, 0)
  let at = 8
  for (const c of kept) {
    out.set(png.subarray(c.at, c.at + 12 + c.length), at)
    at += 12 + c.length
    if (c.type === 'IHDR') {
      for (const a of added) {
        out.set(a, at)
        at += a.length
      }
    }
  }
  return out
}

/** What a PNG says about itself: size, dpi (from pHYs in metres) and its text chunks. */
export function readPngInfo(png: Uint8Array): { width: number; height: number; dpi: number | null; text: Record<string, string> } {
  const chunks = pngChunks(png)
  const ihdr = chunks[0]
  const width = u32(png, ihdr.at + 8)
  const height = u32(png, ihdr.at + 12)
  let dpi: number | null = null
  const text: Record<string, string> = {}
  for (const c of chunks) {
    const d = c.at + 8
    if (c.type === 'pHYs' && png[d + 8] === 1) dpi = Math.round(u32(png, d) * 0.0254 * 100) / 100
    if (c.type === 'iTXt' || c.type === 'tEXt') {
      let k = d
      while (png[k] !== 0 && k < d + c.length) k++
      const key = String.fromCharCode(...png.subarray(d, k))
      let start = k + 1
      if (c.type === 'iTXt') {
        start += 2
        while (png[start] !== 0) start++
        start++
        while (png[start] !== 0) start++
        start++
      }
      text[key] = new TextDecoder().decode(png.subarray(start, d + c.length))
    }
  }
  return { width, height, dpi, text }
}

// ------------------------------------------------------------------ layout

export type Rgb = readonly [number, number, number]

/** One entry of a key: a swatch and what it means. */
export interface FigureKey {
  readonly rgb: Rgb
  readonly label: string
}

/** The legend under the map: a colour bar with ticks, or a list of classes. */
export type FigureLegendSpec =
  | {
      readonly kind: 'bar'
      readonly title: string
      /** Along the bar, 0..1, already composited onto white. */
      readonly stops: readonly { readonly pos: number; readonly rgb: Rgb }[]
      readonly ticks: readonly { readonly pos: number; readonly label: string }[]
      readonly keys: readonly FigureKey[]
    }
  | { readonly kind: 'classes'; readonly title: string; readonly items: readonly FigureKey[]; readonly keys: readonly FigureKey[] }

export interface FigureSpec {
  readonly size: FigureSize
  readonly dpi: number
  readonly title: string
  readonly subtitle: string
  /** The map's width over its height. */
  readonly mapAspect: number
  readonly legend: FigureLegendSpec | null
  readonly caption: string
}

/** The text's width in pixels at `px`, bold or not, in {@link FIGURE_FONT}. */
export type Measure = (text: string, px: number, bold: boolean) => number

/** Arial first: it is on every system that opens the SVG, and its metrics are close to Helvetica's. */
export const FIGURE_FONT = 'Arial, Helvetica, sans-serif'

export interface FigureText {
  readonly text: string
  readonly x: number
  /** Baseline. */
  readonly y: number
  readonly px: number
  readonly bold: boolean
  readonly anchor: 'start' | 'middle' | 'end'
  readonly role: 'title' | 'subtitle' | 'legend' | 'tick' | 'key' | 'caption'
}

export interface FigureRect {
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
}

export interface FigureLayout {
  readonly width: number
  readonly height: number
  readonly dpi: number
  readonly widthMm: number
  readonly map: FigureRect
  readonly texts: readonly FigureText[]
  readonly bar: (FigureRect & { readonly stops: readonly { readonly pos: number; readonly rgb: Rgb }[] }) | null
  readonly ticks: readonly { readonly x: number; readonly y1: number; readonly y2: number }[]
  readonly swatches: readonly (FigureRect & { readonly rgb: Rgb })[]
  /** Hairline width for outlines and ticks, px. */
  readonly hairline: number
}

/** Font sizes in points: 6-8 pt, inside the 5-7 pt most journals ask for, the title one step up. */
const PT = { title: 8, subtitle: 6.5, legend: 6.5, tick: 6, key: 6, caption: 6 } as const

/** Words on lines no wider than `max`; a word wider than a line is broken. */
export function wrapText(text: string, max: number, measure: Measure, px: number, bold = false): string[] {
  const words = text.split(/\s+/).filter(Boolean)
  const lines: string[] = []
  // A link or a long identifier: broken where it overflows; the rest starts the next line.
  const breakWord = (word: string): string => {
    let piece = ''
    for (const ch of word) {
      if (piece && measure(piece + ch, px, bold) > max) {
        lines.push(piece)
        piece = ''
      }
      piece += ch
    }
    return piece
  }
  let line = ''
  for (const word of words) {
    const next = line ? `${line} ${word}` : word
    if (measure(next, px, bold) <= max) {
      line = next
      continue
    }
    if (line) lines.push(line)
    line = measure(word, px, bold) > max ? breakWord(word) : word
  }
  if (line) lines.push(line)
  return lines
}

/** Where everything goes, in pixels at the figure's dpi. */
export function layoutFigure(spec: FigureSpec, measure: Measure): FigureLayout {
  const dpi = spec.dpi
  const widthMm = FIGURE_WIDTH_MM[spec.size]
  const width = mmToPx(widthMm, dpi)
  const mm = (v: number) => (v / 25.4) * dpi
  const px = (role: keyof typeof PT) => ptToPx(PT[role], dpi)
  const margin = Math.round(mm(2.5))
  const content = width - 2 * margin
  const texts: FigureText[] = []
  const hairline = Math.max(1, Math.round(mm(0.15)))
  let y = margin

  const lines = (text: string, role: keyof typeof PT, bold: boolean, lead = 1.25) => {
    const size = px(role)
    for (const line of wrapText(text, content, measure, size, bold)) {
      y += size
      texts.push({ text: line, x: margin, y, px: size, bold, anchor: 'start', role })
      y += size * (lead - 1)
    }
  }
  if (spec.title.trim()) lines(spec.title.trim(), 'title', true)
  if (spec.subtitle.trim()) {
    y += mm(0.6)
    lines(spec.subtitle.trim(), 'subtitle', false)
  }
  y += mm(2)

  // The map: the full width; a globe no taller than 110 mm (two columns would otherwise make it square and very tall).
  let mapW = content
  let mapH = mapW / spec.mapAspect
  const maxH = mm(110)
  if (mapH > maxH) {
    mapH = maxH
    mapW = Math.min(content, mapH * spec.mapAspect)
  }
  const map: FigureRect = { x: Math.round(margin + (content - mapW) / 2), y: Math.round(y), w: Math.round(mapW), h: Math.round(mapH) }
  y = map.y + map.h + mm(2.5)

  let bar: FigureLayout['bar'] = null
  const ticks: { x: number; y1: number; y2: number }[] = []
  const swatches: (FigureRect & { rgb: Rgb })[] = []
  const legend = spec.legend
  const keyRow = (keys: readonly FigureKey[], startX: number) => {
    const size = px('key')
    const sw = mm(2.6)
    let x = startX
    let rowY = y
    for (const key of keys) {
      const w = sw + mm(1) + measure(key.label, size, false)
      if (x > startX && x + w > margin + content) {
        x = startX
        rowY += size * 1.5
      }
      swatches.push({ x: Math.round(x), y: Math.round(rowY), w: Math.round(sw), h: Math.round(sw * 0.75), rgb: key.rgb })
      texts.push({ text: key.label, x: x + sw + mm(1), y: rowY + sw * 0.7, px: size, bold: false, anchor: 'start', role: 'key' })
      x += w + mm(3)
    }
    return rowY + (keys.length ? size * 1.5 : 0)
  }
  if (legend?.kind === 'bar') {
    const barW = Math.min(content, mm(spec.size === 'single' ? 64 : 90))
    const barX = Math.round(margin + (content - barW) / 2)
    // The bar's title over the bar, starting where it starts.
    const size = px('legend')
    for (const line of wrapText(legend.title, barW, measure, size, false)) {
      y += size
      texts.push({ text: line, x: barX, y, px: size, bold: false, anchor: 'start', role: 'legend' })
      y += size * 0.25
    }
    y += mm(0.8)
    bar = { x: barX, y: Math.round(y), w: Math.round(barW), h: Math.round(mm(2.6)), stops: legend.stops }
    y = bar.y + bar.h
    const tickPx = px('tick')
    for (const tk of legend.ticks) {
      if (!(tk.pos >= -1e-9 && tk.pos <= 1 + 1e-9)) continue
      const x = bar.x + tk.pos * bar.w
      ticks.push({ x, y1: y, y2: y + mm(0.9) })
      const anchor = tk.pos < 0.02 ? 'start' : tk.pos > 0.98 ? 'end' : 'middle'
      texts.push({ text: tk.label, x, y: y + mm(0.9) + tickPx, px: tickPx, bold: false, anchor, role: 'tick' })
    }
    y += mm(0.9) + tickPx * 1.6
    if (legend.keys.length) y = keyRow(legend.keys, barX)
  } else if (legend?.kind === 'classes') {
    lines(legend.title, 'legend', false)
    y += mm(0.8)
    y = keyRow([...legend.items, ...legend.keys], margin)
  }
  if (spec.caption.trim()) {
    y += mm(1.5)
    lines(spec.caption.trim(), 'caption', false, 1.3)
  }
  const height = Math.round(y + margin)
  return { width, height, dpi, widthMm, map, texts, bar, ticks, swatches, hairline }
}

// ------------------------------------------------------------------ marks drawn over the map

/** Vectors over the map, in figure pixels: lines (coastlines, graticule, outline) and pins. */
export type FigureMark =
  | { readonly kind: 'lines'; readonly lines: readonly (readonly (readonly [number, number])[])[]; readonly stroke: string; readonly width: number; readonly closed: boolean; readonly role: 'outline' | 'graticule' | 'coast' }
  | { readonly kind: 'pin'; readonly x: number; readonly y: number; readonly r: number; readonly fill: string; readonly label: string; readonly px: number; /** The label right of the dot, or left of it near the right edge. */ readonly anchor?: 'start' | 'end' }

/** A lon/lat to figure pixels, on the Equal Earth map filling `map`. */
export function equalEarthProjector(map: FigureRect): (lon: number, lat: number) => [number, number] {
  const k = map.w / (2 * EE_X_MAX)
  const ky = map.h / (2 * EE_Y_MAX)
  return (lon, lat) => {
    const [x, y] = equalEarth(lon, lat)
    return [map.x + (x + EE_X_MAX) * k, map.y + (EE_Y_MAX - y) * ky]
  }
}

/** The width over the height of the whole Equal Earth world. */
export const EQUAL_EARTH_ASPECT = EE_X_MAX / EE_Y_MAX

/** The world's outline (the ±180° meridians and the flat poles) and a 30° graticule, projected. */
export function equalEarthFrame(map: FigureRect, hairline: number): FigureMark[] {
  const project = equalEarthProjector(map)
  const meridian = (lon: number, step = 2) => {
    const out: [number, number][] = []
    for (let lat = -90; lat <= 90; lat += step) out.push(project(lon, lat))
    return out
  }
  const parallel = (lat: number) => {
    const out: [number, number][] = []
    for (let lon = -180; lon <= 180; lon += 5) out.push(project(lon, lat))
    return out
  }
  const graticule = [
    ...[-60, -30, 0, 30, 60].map(parallel),
    ...[-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150].map((lon) => meridian(lon)),
  ]
  // East side north to south, then the west side south to north; the flat poles join them.
  const outline = [...meridian(180).reverse(), ...meridian(-180)]
  return [
    { kind: 'lines', lines: graticule, stroke: 'rgba(0,0,0,0.22)', width: hairline, closed: false, role: 'graticule' },
    { kind: 'lines', lines: [outline], stroke: '#333333', width: hairline * 1.5, closed: true, role: 'outline' },
  ]
}

/** Coastlines from GeoJSON land polygons (Natural Earth), projected onto the map. */
export function equalEarthCoasts(geojson: unknown, map: FigureRect, hairline: number): FigureMark | null {
  const project = equalEarthProjector(map)
  const rings: [number, number][][] = []
  const add = (coords: number[][]) => {
    if (coords.length > 1) rings.push(coords.map(([lon, lat]) => project(Math.max(-180, Math.min(180, lon)), lat)))
  }
  const visit = (geom: { type?: string; coordinates?: unknown } | null | undefined) => {
    if (!geom) return
    if (geom.type === 'Polygon') (geom.coordinates as number[][][]).forEach(add)
    else if (geom.type === 'MultiPolygon') (geom.coordinates as number[][][][]).forEach((p) => p.forEach(add))
  }
  const g = geojson as { type?: string; features?: { geometry?: unknown }[]; geometry?: unknown }
  if (g?.type === 'FeatureCollection') g.features?.forEach((f) => visit(f.geometry as never))
  else if (g?.type === 'Feature') visit(g.geometry as never)
  else visit(g as never)
  if (!rings.length) return null
  return { kind: 'lines', lines: rings, stroke: '#1a1a1a', width: hairline, closed: true, role: 'coast' }
}

/**
 * The whole world on the Equal Earth projection as RGBA pixels, `width`
 * wide: each row is one latitude (the projection is pseudocylindrical),
 * so the inverse is one Newton solve per row and a multiplication per
 * pixel. `sample` writes the colour at a lon/lat into `out` at `offset`;
 * pixels outside the world's outline stay transparent.
 */
export function equalEarthRaster(
  width: number,
  height: number,
  sample: (lat: number, lon: number, out: Uint8ClampedArray, offset: number) => void,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(width * height * 4)
  const xPerPx = (2 * EE_X_MAX) / width
  for (let j = 0; j < height; j++) {
    const row = equalEarthRow(EE_Y_MAX - ((j + 0.5) / height) * 2 * EE_Y_MAX)
    if (!row) continue
    for (let i = 0; i < width; i++) {
      const lon = (-EE_X_MAX + (i + 0.5) * xPerPx) * row.lonPerX
      if (lon < -180 || lon > 180) continue
      sample(row.lat, lon, out, (j * width + i) * 4)
    }
  }
  return out
}

// ------------------------------------------------------------------ SVG

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

const r1 = (v: number) => Math.round(v * 10) / 10

function rgbCss(c: Rgb): string {
  return `rgb(${c[0]},${c[1]},${c[2]})`
}

export interface FigureSvgOptions {
  /** The map's raster as a data URL (PNG). */
  readonly mapHref: string
  readonly marks: readonly FigureMark[]
  /** For `<title>` and `<desc>`: the figure's title, and the caption with how it was made. */
  readonly title: string
  readonly description: string
}

function pathOf(lines: readonly (readonly (readonly [number, number])[])[], closed: boolean): string {
  return lines.map((pts) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${r1(x)} ${r1(y)}`).join('') + (closed ? 'Z' : '')).join('')
}

/**
 * The figure as SVG: physical size in millimetres, a pixel viewBox at the
 * figure's dpi, every text and the colour bar as vectors, the map as an
 * embedded raster clipped to its frame.
 */
export function figureSvg(layout: FigureLayout, opts: FigureSvgOptions): string {
  const heightMm = (layout.height / layout.dpi) * 25.4
  const out: string[] = []
  out.push(`<?xml version="1.0" encoding="UTF-8"?>`)
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="${layout.widthMm}mm" height="${r1(heightMm)}mm" viewBox="0 0 ${layout.width} ${layout.height}" font-family="${FIGURE_FONT}">`)
  out.push(`<title>${esc(opts.title)}</title>`)
  out.push(`<desc>${esc(opts.description)}</desc>`)
  out.push(`<defs><clipPath id="fig-map-clip"><rect x="${layout.map.x}" y="${layout.map.y}" width="${layout.map.w}" height="${layout.map.h}"/></clipPath>`)
  if (layout.bar) {
    out.push(`<linearGradient id="fig-bar" x1="0" y1="0" x2="1" y2="0">${layout.bar.stops.map((s) => `<stop offset="${r1(s.pos * 100)}%" stop-color="${rgbCss(s.rgb)}"/>`).join('')}</linearGradient>`)
  }
  out.push(`</defs>`)
  out.push(`<rect width="${layout.width}" height="${layout.height}" fill="#ffffff"/>`)
  out.push(`<g id="map" clip-path="url(#fig-map-clip)"><image x="${layout.map.x}" y="${layout.map.y}" width="${layout.map.w}" height="${layout.map.h}" preserveAspectRatio="none" href="${opts.mapHref}" xlink:href="${opts.mapHref}"/>`)
  for (const m of opts.marks) {
    if (m.kind === 'lines') {
      out.push(`<path class="${m.role}" d="${pathOf(m.lines, m.closed)}" fill="none" stroke="${m.stroke}" stroke-width="${r1(m.width)}" stroke-linejoin="round"/>`)
    }
  }
  out.push(`</g>`)
  for (const m of opts.marks) {
    if (m.kind !== 'pin') continue
    out.push(`<g class="pin"><circle cx="${r1(m.x)}" cy="${r1(m.y)}" r="${r1(m.r)}" fill="${esc(m.fill)}" stroke="#ffffff" stroke-width="${r1(m.r * 0.35)}"/>`
      + `<text x="${r1(m.anchor === 'end' ? m.x - m.r * 1.8 : m.x + m.r * 1.8)}" y="${r1(m.y + m.px * 0.35)}"${m.anchor === 'end' ? ' text-anchor="end"' : ''} font-size="${r1(m.px)}" font-weight="bold" fill="#111111" stroke="#ffffff" stroke-width="${r1(m.px * 0.22)}" paint-order="stroke" stroke-linejoin="round">${esc(m.label)}</text></g>`)
  }
  if (layout.bar) {
    const b = layout.bar
    out.push(`<rect id="colour-bar" x="${b.x}" y="${b.y}" width="${b.w}" height="${b.h}" fill="url(#fig-bar)" stroke="#333333" stroke-width="${layout.hairline}"/>`)
  }
  for (const tk of layout.ticks) out.push(`<line class="tick" x1="${r1(tk.x)}" y1="${r1(tk.y1)}" x2="${r1(tk.x)}" y2="${r1(tk.y2)}" stroke="#333333" stroke-width="${layout.hairline}"/>`)
  for (const s of layout.swatches) out.push(`<rect class="key" x="${s.x}" y="${s.y}" width="${s.w}" height="${s.h}" fill="${rgbCss(s.rgb)}" stroke="#333333" stroke-width="${layout.hairline}"/>`)
  for (const tx of layout.texts) {
    out.push(`<text class="${tx.role}" x="${r1(tx.x)}" y="${r1(tx.y)}" font-size="${r1(tx.px)}"${tx.bold ? ' font-weight="bold"' : ''}${tx.anchor !== 'start' ? ` text-anchor="${tx.anchor}"` : ''} fill="${tx.role === 'caption' ? '#333333' : '#111111'}">${esc(tx.text)}</text>`)
  }
  out.push(`</svg>`)
  return out.join('\n')
}

// ------------------------------------------------------------------ numbers on the figure

/** A tick label: up to `sig` significant digits, a true minus sign, no locale grouping surprises. */
export function tickLabel(value: number, signed = false, sig = 4): string {
  if (!Number.isFinite(value)) return ''
  if (value === 0 || Math.abs(value) < 1e-12) return '0'
  const v = Number(value.toPrecision(sig))
  const abs = Math.abs(v)
  const text = abs >= 1e-4 && abs < 1e7 ? String(abs) : abs.toExponential(2).replace('e+', 'e')
  return `${v < 0 ? '−' : signed ? '+' : ''}${text}`
}

/**
 * Ticks for a change bar: −limit, −limit/2, 0, +limit/2, +limit. AYNI: with
 * the viewer's range (`min`), the left half runs to `min` instead, as the
 * map paints each side over its own end.
 */
export function changeTicks(limit: number, min = -limit): { pos: number; label: string }[] {
  return [-1, -0.5, 0, 0.5, 1].map((f) => ({ pos: (f + 1) / 2, label: tickLabel(f < 0 ? -f * min : f * limit, true) }))
}

/** An RGBA colour put down on white, as the figure's ground. */
export function onWhite(r: number, g: number, b: number, a: number): Rgb {
  const k = a / 255
  return [Math.round(r * k + 255 * (1 - k)), Math.round(g * k + 255 * (1 - k)), Math.round(b * k + 255 * (1 - k))]
}
